import React, { useState, useEffect, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  type ICellRendererParams,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import { type WatchlistGroup, type WatchlistItem } from '../types';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

const WatchlistManagement: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const axiosInstance = getMainAxios();

  const [groups, setGroups] = useState<WatchlistGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [prodNms, setProdNms] = useState<Record<string, string>>({});
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ prodId: string; prodNm: string; market: string }>>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // 그룹 목록 조회
  const fetchGroups = async () => {
    try {
      const res = await axiosInstance.get('/api/v1/watchlist/groups');
      setGroups(res.data || []);
      if (res.data?.length > 0 && !selectedGroupId) {
        setSelectedGroupId(res.data[0].id);
      }
    } catch (err) {
      console.error('그룹 조회 실패:', err);
    }
  };

  // 종목 목록 조회
  const fetchItems = async (groupId: number) => {
    try {
      const res = await axiosInstance.get(`/api/v1/watchlist/groups/${groupId}/items`);
      const itemsData = res.data || [];
      setItems(itemsData);
      // 종목명 조회
      const prodIds = itemsData.map((i: WatchlistItem) => i.prodId);
      if (prodIds.length > 0) fetchProdNms(prodIds);
    } catch (err) {
      console.error('종목 조회 실패:', err);
    }
  };

  // 종목명 조회
  const fetchProdNms = async (prodIds: string[]) => {
    try {
      const qs = prodIds.map(id => `prodIds=${encodeURIComponent(id)}`).join('&');
      const res = await axiosInstance.get(`/api/v1/stocks/prod-nms?${qs}`);
      if (res.data?.success && res.data?.data) {
        setProdNms(prev => ({ ...prev, ...res.data.data }));
      }
    } catch (err) {
      console.error('종목명 조회 실패:', err);
    }
  };

  // 그룹 생성
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) { alert('그룹명을 입력하세요.'); return; }
    try {
      const res = await axiosInstance.post('/api/v1/watchlist/groups', {
        name: newGroupName.trim(),
        seqNo: groups.length
      });
      if (res.data?.success) {
        setNewGroupName('');
        fetchGroups();
      }
    } catch (err) {
      console.error('그룹 생성 실패:', err);
    }
  };

  // 그룹 삭제
  const handleDeleteGroup = async (id: number) => {
    if (!confirm('그룹을 삭제하시겠습니까? 포함된 종목도 모두 삭제됩니다.')) return;
    try {
      await axiosInstance.delete(`/api/v1/watchlist/groups/${id}`);
      if (selectedGroupId === id) {
        setSelectedGroupId(null);
        setItems([]);
      }
      fetchGroups();
    } catch (err) {
      console.error('그룹 삭제 실패:', err);
    }
  };

  // 그룹명 수정
  const handleUpdateGroup = async (id: number) => {
    if (!editGroupName.trim()) return;
    try {
      await axiosInstance.put(`/api/v1/watchlist/groups/${id}`, { name: editGroupName.trim() });
      setEditingGroupId(null);
      setEditGroupName('');
      fetchGroups();
    } catch (err) {
      console.error('그룹 수정 실패:', err);
    }
  };

  // 종목 검색
  const handleSearch = async (term: string) => {
    if (!term.trim() || term.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    try {
      const res = await axiosInstance.get('/api/v1/stocks/search', { params: { term: term.trim(), limit: 20 } });
      if (res.data?.success && res.data?.data) {
        setSearchResults(res.data.data);
        setShowSearchResults(true);
      }
    } catch (err) {
      console.error('종목 검색 실패:', err);
    }
  };

  // 종목 추가
  const handleAddItem = async (prodId: string) => {
    if (!selectedGroupId) { alert('그룹을 먼저 선택하세요.'); return; }
    try {
      const res = await axiosInstance.post(`/api/v1/watchlist/groups/${selectedGroupId}/items`, { prodId });
      if (res.data?.success) {
        fetchItems(selectedGroupId);
        setSearchTerm('');
        setShowSearchResults(false);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || '종목 추가 실패');
    }
  };

  // 종목 삭제
  const handleDeleteItem = async (id: number) => {
    try {
      await axiosInstance.delete(`/api/v1/watchlist/items/${id}`);
      if (selectedGroupId) fetchItems(selectedGroupId);
    } catch (err) {
      console.error('종목 삭제 실패:', err);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  useEffect(() => {
    if (selectedGroupId) fetchItems(selectedGroupId);
    else setItems([]);
  }, [selectedGroupId]);

  // 검색 디바운싱
  useEffect(() => {
    const timer = setTimeout(() => { handleSearch(searchTerm); }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const itemColumnDefs: ColDef<WatchlistItem>[] = [
    { headerName: '순서', field: 'seqNo', width: 70, cellStyle: { textAlign: 'center' } },
    { headerName: '종목코드', field: 'prodId', width: 110, cellStyle: { textAlign: 'center' } },
    {
      headerName: '종목명', width: 180,
      valueGetter: (params) => prodNms[params.data?.prodId || ''] || '',
    },
    {
      headerName: '추가일시', field: 'addedT', width: 160,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString('ko-KR') : '',
    },
    {
      headerName: '삭제', width: 70, sortable: false, filter: false,
      cellRenderer: (params: ICellRendererParams<WatchlistItem>) => (
        <button
          onClick={() => params.data && handleDeleteItem(params.data.id)}
          style={{ color: '#ef4444', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}
        >×</button>
      )
    }
  ];

  const defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 0,
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '16px' }}>
        {/* 좌측: 그룹 목록 */}
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af', margin: '0 0 12px 0' }}>관심종목 그룹</h3>

          {/* 그룹 추가 */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
            <input
              type="text" value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGroup(); }}
              placeholder="새 그룹명"
              style={{ flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
            />
            <button onClick={handleCreateGroup}
              style={{ padding: '6px 12px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
              추가
            </button>
          </div>

          {/* 그룹 리스트 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {groups.map(g => (
              <div key={g.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '8px',
                  backgroundColor: selectedGroupId === g.id ? '#dbeafe' : '#f9fafb',
                  border: selectedGroupId === g.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  borderRadius: '4px', cursor: 'pointer'
                }}
                onClick={() => setSelectedGroupId(g.id)}
              >
                {editingGroupId === g.id ? (
                  <input
                    type="text" value={editGroupName}
                    onChange={(e) => setEditGroupName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateGroup(g.id); if (e.key === 'Escape') setEditingGroupId(null); }}
                    onBlur={() => handleUpdateGroup(g.id)}
                    autoFocus
                    style={{ flex: 1, padding: '2px 6px', border: '1px solid #3b82f6', borderRadius: '3px', fontSize: '12px' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: selectedGroupId === g.id ? '600' : '400', color: '#1e40af' }}>
                    {g.name}
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingGroupId(g.id); setEditGroupName(g.name); }}
                  style={{ padding: '2px 6px', backgroundColor: 'transparent', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', color: '#6b7280', cursor: 'pointer' }}>
                  수정
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.id); }}
                  style={{ padding: '2px 6px', backgroundColor: 'transparent', border: '1px solid #fca5a5', borderRadius: '3px', fontSize: '11px', color: '#ef4444', cursor: 'pointer' }}>
                  삭제
                </button>
              </div>
            ))}
            {groups.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>그룹이 없습니다.</div>
            )}
          </div>
        </div>

        {/* 우측: 종목 목록 */}
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af', margin: 0 }}>
              {selectedGroupId ? `종목 목록 — ${groups.find(g => g.id === selectedGroupId)?.name || ''}` : '그룹을 선택하세요'}
            </h3>
          </div>

          {/* 종목 검색 및 추가 */}
          {selectedGroupId && (
            <div style={{ position: 'relative', marginBottom: '12px', display: 'flex', gap: '4px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text" value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => { if (searchResults.length > 0) setShowSearchResults(true); }}
                  onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                  placeholder="종목코드/종목명 검색 후 클릭하여 추가"
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
                />
                {showSearchResults && searchResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '2px',
                    backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '300px', overflowY: 'auto', zIndex: 1000
                  }}>
                    {searchResults.map((r, idx) => {
                      const alreadyAdded = items.some(i => i.prodId === r.prodId);
                      return (
                        <div key={idx}
                          onMouseDown={(e) => { e.preventDefault(); if (!alreadyAdded) handleAddItem(r.prodId); }}
                          style={{
                            padding: '8px 12px', cursor: alreadyAdded ? 'default' : 'pointer',
                            borderBottom: idx < searchResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                            backgroundColor: alreadyAdded ? '#f3f4f6' : 'white'
                          }}
                          onMouseEnter={(e) => { if (!alreadyAdded) e.currentTarget.style.backgroundColor = '#f0f9ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = alreadyAdded ? '#f3f4f6' : 'white'; }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <span style={{ fontSize: '12px', fontWeight: '600', color: alreadyAdded ? '#9ca3af' : '#1e40af' }}>{r.prodNm}</span>
                              <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>{r.prodId} ({r.market})</span>
                            </div>
                            {alreadyAdded && <span style={{ fontSize: '10px', color: '#22c55e' }}>추가됨</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AG Grid 종목 테이블 */}
          {selectedGroupId && (
            <div className="ag-theme-quartz" style={{ height: Math.max(200, Math.min(500, 42 + items.length * 32)), width: '100%' }}>
              <AgGridReact {...copyMenu.gridProps}
                rowData={items}
                columnDefs={itemColumnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                headerHeight={36}
                rowHeight={32}
              />
            </div>
          )}

          {selectedGroupId && items.length === 0 && (
            <div style={{ padding: '30px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
              종목을 검색하여 추가하세요.
            </div>
          )}
        </div>
      </div>
      {copyMenu.menu}
    </div>
  );
};

export default WatchlistManagement;
